/**
 * Pure parts of box-select and double-click select: the click-vs-drag
 * threshold, the marquee rectangle, which projected units fall inside it,
 * and the same-type filter. Screen coordinates are client pixels.
 */
import * as THREE from 'three';

/** Pointer travel (px) past which a press is a drag, not a click. */
export const DRAG_PX = 5;
/** Two clicks on the same unit within this many ms are a double-click. */
export const DOUBLE_CLICK_MS = 350;

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** A unit's drawn position projected to the screen. */
export interface ScreenUnit extends Point {
  id: string;
}

/** Whether moving from `a` to `b` is a drag rather than a click. */
export function exceedsDrag(a: Point, b: Point, threshold: number = DRAG_PX): boolean {
  return Math.hypot(b.x - a.x, b.y - a.y) > threshold;
}

/** The rectangle between two opposite corners, whichever way it was dragged. */
export function normaliseRect(a: Point, b: Point): Rect {
  return {
    left: Math.min(a.x, b.x),
    top: Math.min(a.y, b.y),
    right: Math.max(a.x, b.x),
    bottom: Math.max(a.y, b.y),
  };
}

export function pointInRect(p: Point, r: Rect): boolean {
  return p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;
}

/** Ids of the units whose screen position is inside `rect`. */
export function idsInRect(units: readonly ScreenUnit[], rect: Rect): string[] {
  return units.filter((u) => pointInRect(u, rect)).map((u) => u.id);
}

/** The ids whose unit type is `type`. */
export function idsOfType(
  ids: readonly string[],
  typeOf: (id: string) => string | undefined,
  type: string
): string[] {
  return ids.filter((id) => typeOf(id) === type);
}

/** `ids` added to `prev` (those already in it are not repeated). */
export function unionIds(prev: readonly string[], ids: readonly string[]): string[] {
  return [...prev, ...ids.filter((id) => !prev.includes(id))];
}

/** A click on a unit, remembered to spot a double-click. */
export interface UnitClick {
  id: string;
  time: number;
}

/** Whether a click on `id` at `now` (ms) follows `last` on the same unit closely enough. */
export function isDoubleClick(last: UnitClick | null, id: string, now: number): boolean {
  return last !== null && last.id === id && now - last.time <= DOUBLE_CLICK_MS;
}

const scratch = new THREE.Vector3();

/**
 * Where `picks` (world x/z at height `y`) land on screen, in client pixels
 * over `area`; those behind the camera or outside the view are left out.
 */
export function projectToScreen(
  camera: THREE.Camera,
  picks: readonly { id: string; x: number; z: number }[],
  area: { left: number; top: number; width: number; height: number },
  y: number
): ScreenUnit[] {
  const out: ScreenUnit[] = [];
  for (const p of picks) {
    scratch.set(p.x, y, p.z).project(camera);
    if (scratch.z < -1 || scratch.z > 1 || Math.abs(scratch.x) > 1 || Math.abs(scratch.y) > 1) {
      continue;
    }
    out.push({
      id: p.id,
      x: area.left + ((scratch.x + 1) / 2) * area.width,
      y: area.top + ((1 - scratch.y) / 2) * area.height,
    });
  }
  return out;
}
